/**
 * RoInput - 表单输入框 Web 组件
 *
 * 功能特性：
 * - Form-Associated Custom Element，可与父级 <form> 集成（value、disabled、reset 等）
 * - 状态：聚焦 focused、校验失败 error、禁用 disabled；错误文案在 Shadow DOM 内展示
 * - 非 float-label：尺寸 size：large | medium | small（默认 medium）；`disable-adaptation="true"` 关闭窄屏高度降档（见样式）
 * - 浮动标签 float-label：独立布局
 * - 前置/后置图标（slot：prefix-icon、suffix-icon）
 * - 国际电话区号：仅当宿主传入非空 `dial-codes` JSON 数组时启用（无默认列表）；与 `type="tel"` 搭配；提交值为 `+{code} {national}`；Others 文案由 `phone-others-label` 或 RoFormLocale.phone_others 提供
 * - 校验顺序：必填 → type（email / tel / intl-tel / number）→ pattern → 长度 → number 范围 → validate-fn
 * - validate-on-input、事件、方法同前
 *
 * 国际区号模式属性：
 * - dial-codes='[86, 81]' 或 '["86","81"]' — JSON 数组；亦兼容旧版 { "code", "label?" } 对象项
 * - phone-others-label — Others 展示文案（建议 Liquid 翻译）
 */
class RoInput extends HTMLElement {
  static formAssociated = true;

  static INPUT_ATTRS = ["pattern", "min", "max"];

  static DEFAULT_MESSAGES = {
    required: "This field is required",
    invalid_email: "Please enter a valid email",
    invalid_phone: "Please enter a valid phone number",
    invalid_phone_intl: "Please enter a valid country code and phone number",
    invalid_number: "Please enter a valid number",
    pattern: "Invalid format",
    min_length: "Minimum {{ min }} characters required",
    max_length: "Maximum {{ max }} characters allowed",
    min_value: "Minimum value is {{ min }}",
    max_value: "Maximum value is {{ max }}",
  };

  static DIAL_PANEL_STYLE_ID = "ro-input-dial-panel-styles";

  static _digitsOnly(s) {
    return String(s || "").replace(/\D/g, "");
  }

  /** @returns {Array<{code:string,label:string}>|null} */
  static _parseDialCodesList(raw) {
    if (raw == null || String(raw).trim() === "") return null;
    try {
      const arr = JSON.parse(String(raw));
      if (!Array.isArray(arr) || arr.length === 0) return null;
      const out = [];
      for (const item of arr) {
        if (item == null) continue;
        if (typeof item === "number" && Number.isFinite(item)) {
          const code = RoInput._digitsOnly(String(Math.trunc(item)));
          if (code) out.push({ code, label: "" });
          continue;
        }
        if (typeof item === "string") {
          const code = RoInput._digitsOnly(item);
          if (code) out.push({ code, label: "" });
          continue;
        }
        if (typeof item === "object") {
          const code = RoInput._digitsOnly(item.code != null ? String(item.code) : "");
          if (!code) continue;
          const label = item.label != null && String(item.label).trim() !== "" ? String(item.label) : "";
          out.push({ code, label });
        }
      }
      return out.length ? out : null;
    } catch {
      return null;
    }
  }

  /** @returns {{dial:string,national:string}|null} */
  static _parseIntlString(str) {
    const s = String(str || "").trim();
    if (!s) return null;
    const m = s.match(/^\s*\+?\s*(\d{1,4})\s+(.+?)\s*$/);
    if (!m) return null;
    return { dial: m[1], national: RoInput._digitsOnly(m[2]) };
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._internals = this.attachInternals();

    this._value = "";
    this._isFocused = false;
    this._isError = false;
    this._errorMessage = "";

    this._intlMode = false;
    this._intlDialList = null;
    /** @type {number} >=0 预设下标； -1 表示 Others */
    this._intlSelectedIndex = 0;
    /** Others 模式下的区号（面板未创建时仍保留，供 compose / 回填） */
    this._intlCustomDial = "";

    this._dialPanelEl = null;
    this._dialOpen = false;
    this._dialCustomInputEl = null;
    this._dialTriggerEl = null;
    this._dialTriggerTextEl = null;

    this._suspendValueAttr = false;
    this._listenersBound = false;

    this._onInput = this._onInput.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onContainerClick = this._onContainerClick.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
    this._onDialTriggerClick = this._onDialTriggerClick.bind(this);
    this._onDocPointerDown = this._onDocPointerDown.bind(this);
    this._onWinScrollResize = this._onWinScrollResize.bind(this);
    this._onDialPanelClick = this._onDialPanelClick.bind(this);
    this._onDialCustomInput = this._onDialCustomInput.bind(this);

    this._render();
  }

  _detectIntlMode() {
    const list = RoInput._parseDialCodesList(this.getAttribute("dial-codes"));
    return !!(list && list.length);
  }

  /** 仅规范非空 `size`；空值由 `_syncDefaultSizeAttribute` 写回 medium。非法值 → medium。 */
  static _normalizeSize(raw) {
    const s = String(raw).trim().toLowerCase();
    if (s === "large" || s === "medium" || s === "small") return s;
    return "medium";
  }

  _getSharedStyles() {
    return `
        :host {
          display: inline-flex;
          flex-direction: column;
          width: 100%;
          --ro-field-padding: 12px 16px;
        }
        :host([dial-intl]) .input-container { gap: 8px; }
        .input-container {
          display: flex;
          align-items: center;
          gap: 12px;
          box-sizing: border-box;
          padding: var(--ro-field-padding);
          background: var(--bg-white-color, #fff);
          border: 1px solid var(--border-color, #e1e1e1);
          border-radius: var(--ro-field-border-radius, 4px);
          transition: border-color 0.2s ease;
          cursor: text;
        }
        :host(:not([float-label])[size="large"]) .input-container {
          height: 56px;
          gap: 12px;
        }
        :host(:not([float-label])[size="medium"]) .input-container,
        :host(:not([float-label]):not([size])) .input-container {
          height: var(--ro-field-medium-height, 48px);
          gap: 12px;
        }
        :host(:not([float-label])[size="small"]) .input-container {
          height: 44px;
          gap: 8px;
        }
        :host([focused]) .input-container { border-color: var(--main-color, #161616); }
        :host([error]) .input-container { border-color: var(--error-color, #d93840); }
        :host([disabled]) .input-container {
          background: rgba(225, 225, 225, 0.4);
          cursor: not-allowed;
        }
        .dial-trigger {
          display: flex;
          align-items: center;
          gap: 2px;
          flex-shrink: 0;
          border: none;
          background: transparent;
          cursor: pointer;
          padding: 0;
          margin: 0;
          font: inherit;
          color: var(--main-color, #161616);
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
        }
        .dial-trigger:disabled { cursor: not-allowed; opacity: 0.6; }
        .dial-chevron {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          flex-shrink: 0;
          color: var(--second-color, #6c6d6e);
        }
        .dial-chevron svg {
          display: block;
          transition: transform 0.2s ease;
          transform: rotate(0deg);
          transform-origin: 50% 50%;
        }
        :host([dial-panel-open]) .dial-chevron svg {
          transform: rotate(180deg);
        }
        .icon-slot {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          flex-shrink: 0;
          color: var(--second-color, #6c6d6e);
        }
        .icon-slot.empty { display: none; }
        .icon-slot::slotted(*) { width: 24px; height: 24px; display: block; }
        .input-wrapper {
          flex: 1;
          min-width: 0;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        :host(:not([float-label])[size="large"]) .input-wrapper {
          min-height: 32px;
        }
        :host(:not([float-label])[size="medium"]) .input-wrapper,
        :host(:not([float-label]):not([size])) .input-wrapper {
          min-height: 26px;
        }
        :host(:not([float-label])[size="small"]) .input-wrapper {
          min-height: 24px;
        }
        :host([float-label]) .input-container {
          height: 56px;
          gap: 12px;
        }
        :host([float-label][has-value]) .input-wrapper,
        :host([float-label][focused]) .input-wrapper { justify-content: flex-start; }
        .label {
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          color: var(--second-color, #6c6d6e);
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          pointer-events: none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
        :host(:dir(rtl)) .label { left: auto; right: 0; }
        :host([float-label][has-value]) .label,
        :host([float-label][focused]) .label {
          position: static;
          transform: none;
          font-size: 12px;
          line-height: 16px;
        }
        .input {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          color: var(--main-color, #161616);
          font-weight: 400;
          font-size: 16px;
          line-height: 1.5;
          padding: 0;
          margin: 0;
        }
        :host(:not([float-label])[size="medium"]) .input,
        :host(:not([float-label]):not([size])) .input,
        :host(:not([float-label])[size="small"]) .input {
          font-size: 14px;
          line-height: 20px;
        }
        :host(:not([float-label])) .input::placeholder {
          color: var(--second-color, #6c6d6e);
          font-size: 14px;
          opacity: 1;
        }
        :host([float-label]) .input::placeholder { opacity: 0; }
        .input:disabled {
          cursor: not-allowed;
          color: var(--second-color, #6c6d6e);
        }
        .error-message {
          display: none;
          margin-top: 8px;
          padding: 0 16px;
          color: var(--error-color, #d93840);
          font-weight: 400;
          font-size: var(--error-font-size, 12px);
          line-height: var(--error-line-height, 16px);
        }
        :host([error]) .error-message { display: block; }
        @media (max-width: 1183px) {
          :host(:not([disable-adaptation="true"]):not([float-label])[size="large"]) .input-container {
            height: var(--ro-field-medium-height, 48px);
            gap: 12px;
          }
          :host(:not([disable-adaptation="true"]):not([float-label])[size="medium"]) .input-container,
          :host(:not([disable-adaptation="true"]):not([float-label]):not([size])) .input-container {
            height: var(--ro-field-medium-height-mobile, 44px);
            gap: 8px;
          }
          :host(:not([disable-adaptation="true"])[float-label]) .input-container {
            height: 48px;
            gap: 12px;
          }
          :host(:not([disable-adaptation="true"]):not([float-label])[size="large"]) .input-wrapper {
            min-height: 26px;
          }
          :host(:not([disable-adaptation="true"]):not([float-label])[size="medium"]) .input-wrapper,
          :host(:not([disable-adaptation="true"]):not([float-label]):not([size])) .input-wrapper {
            min-height: 24px;
          }
          :host(:not([disable-adaptation="true"])[float-label]) .input-wrapper {
            min-height: 26px;
          }
          .error-message { padding: 0 12px; }
        }
    `;
  }

  _render() {
    const wasIntl = this._intlMode;
    this._intlMode = this._detectIntlMode();
    this._intlDialList = this._intlMode ? RoInput._parseDialCodesList(this.getAttribute("dial-codes")) : null;

    if (this._intlMode && this._intlDialList) {
      if (!wasIntl || this._intlSelectedIndex < 0 || this._intlSelectedIndex >= this._intlDialList.length) {
        this._intlSelectedIndex = 0;
      }
    } else {
      this._intlSelectedIndex = 0;
    }

    const inputId = `ro-input-${Math.random().toString(36).slice(2, 9)}`;

    const intlBlock = this._intlMode
      ? `
        <button type="button" class="dial-trigger" part="dial-trigger" aria-haspopup="listbox" aria-expanded="false">
          <span class="dial-trigger-text"></span>
          <span class="dial-chevron" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 10L12 15L18 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>`
      : "";

    this.shadowRoot.innerHTML = `
      <style>${this._getSharedStyles()}</style>
      <div class="input-container">
        ${intlBlock}
        <slot name="prefix-icon" class="icon-slot"></slot>
        <div class="input-wrapper">
          <label class="label" for="${inputId}"></label>
          <input class="input" id="${inputId}" />
        </div>
        <slot name="suffix-icon" class="icon-slot"></slot>
      </div>
      <div class="error-message"></div>
    `;

    this._inputEl = this.shadowRoot.querySelector(".input");
    this._labelEl = this.shadowRoot.querySelector(".label");
    this._errorEl = this.shadowRoot.querySelector(".error-message");
    this._containerEl = this.shadowRoot.querySelector(".input-container");
    this._prefixSlot = this.shadowRoot.querySelector('slot[name="prefix-icon"]');
    this._suffixSlot = this.shadowRoot.querySelector('slot[name="suffix-icon"]');
    this._dialTriggerEl = this.shadowRoot.querySelector(".dial-trigger") || null;
    this._dialTriggerTextEl = this.shadowRoot.querySelector(".dial-trigger-text") || null;

    this.toggleAttribute("dial-intl", this._intlMode);
    if (!this._intlMode) this.removeAttribute("dial-panel-open");

    if (this._dialTriggerEl) {
      this._dialTriggerEl.addEventListener("click", this._onDialTriggerClick);
    }

    this._destroyDialPanel();
    this._refreshDialTriggerText();
  }

  _ensureDialPanelStyles() {
    if (document.getElementById(RoInput.DIAL_PANEL_STYLE_ID)) return;
    const st = document.createElement("style");
    st.id = RoInput.DIAL_PANEL_STYLE_ID;
    st.textContent = `
.ro-input-dial-panel {
  position: fixed;
  z-index: 10050;
  margin: 0;
  width: 151px;
  min-width: 151px;
  max-width: 151px;
  max-height: min(320px, 70vh);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--ro-select-scrollbar-thumb, #d1d1d1) transparent;
  background: var(--bg-white-color, #fff);
  border: none;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(22, 22, 22, 0.15);
  padding: 8px 0;
  box-sizing: border-box;
  font-family: inherit;
}
.ro-input-dial-panel::-webkit-scrollbar { width: 6px; }
.ro-input-dial-panel::-webkit-scrollbar-track { background: transparent; border-radius: 3px; }
.ro-input-dial-panel::-webkit-scrollbar-thumb {
  background: var(--ro-select-scrollbar-thumb, #d1d1d1);
  border-radius: 3px;
}
.ro-input-dial-panel::-webkit-scrollbar-thumb:hover {
  background: var(--ro-select-scrollbar-thumb-hover, #b8b8b8);
}
.ro-input-dial-panel .ro-input-dial-opt {
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 10px 16px;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--main-color, #161616);
  text-align: left;
  box-sizing: border-box;
}
.ro-input-dial-panel[dir="rtl"] .ro-input-dial-opt { text-align: right; }
.ro-input-dial-panel .ro-input-dial-opt:hover { background: rgba(0,0,0,0.04); }
.ro-input-dial-panel .ro-input-dial-others {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  padding: 4px 0 10px;
  box-sizing: border-box;
}
.ro-input-dial-panel .ro-input-dial-others-label {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: var(--main-color, #161616);
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
}
.ro-input-dial-panel .ro-input-dial-custom-wrap {
  margin: 0 16px;
  box-sizing: border-box;
  border: 1px solid var(--border-color, #e1e1e1);
  border-radius: 4px;
  background: var(--bg-white-color, #fff);
  padding: 8px 16px;
  display: flex;
  align-items: center;
}
.ro-input-dial-panel .ro-input-dial-custom {
  flex: 1;
  min-width: 0;
  border: none;
  padding: 0;
  margin: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  color: var(--main-color, #161616);
  box-sizing: border-box;
}
`;
    document.head.appendChild(st);
  }

  _ensureDialPanel() {
    if (!this._intlMode || this._dialPanelEl) return;
    this._ensureDialPanelStyles();
    this._dialPanelEl = document.createElement("div");
    this._dialPanelEl.className = "ro-input-dial-panel";
    if (getComputedStyle(this).direction === "rtl") this._dialPanelEl.dir = "rtl";
    this._dialPanelEl.hidden = true;
    this._dialPanelEl.setAttribute("role", "listbox");
    this._dialPanelEl.addEventListener("click", this._onDialPanelClick);
    document.body.appendChild(this._dialPanelEl);
  }

  _destroyDialPanel() {
    this._closeDialPanel();
    if (this._dialPanelEl) {
      this._dialPanelEl.removeEventListener("click", this._onDialPanelClick);
      this._dialPanelEl.remove();
      this._dialPanelEl = null;
    }
    this._dialCustomInputEl = null;
  }

  _populateDialPanel() {
    if (!this._dialPanelEl || !this._intlDialList) return;
    this._dialPanelEl.innerHTML = "";
    const frag = document.createDocumentFragment();
    this._intlDialList.forEach((item, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ro-input-dial-opt";
      btn.setAttribute("role", "option");
      btn.dataset.dialIndex = String(i);
      btn.textContent = item.label || item.code;
      frag.appendChild(btn);
    });
    const others = document.createElement("div");
    others.className = "ro-input-dial-others";
    others.innerHTML =
      '<div class="ro-input-dial-others-label"></div>' +
      '<div class="ro-input-dial-custom-wrap">' +
      '<input type="tel" class="ro-input-dial-custom" inputmode="numeric" pattern="[0-9]*" maxlength="4" autocomplete="off" />' +
      "</div>";
    frag.appendChild(others);
    this._dialPanelEl.appendChild(frag);
    this._dialCustomInputEl = others.querySelector(".ro-input-dial-custom");
    const lab = others.querySelector(".ro-input-dial-others-label");
    lab.textContent = this._getOthersLabelText();
    this._dialCustomInputEl.removeAttribute("placeholder");
    if (this._intlSelectedIndex < 0) {
      this._dialCustomInputEl.value = this._intlCustomDial;
    } else {
      this._dialCustomInputEl.value = "";
    }
    this._dialCustomInputEl.addEventListener("input", this._onDialCustomInput);
    this._dialCustomInputEl.addEventListener("focus", () => {
      this._intlSelectedIndex = -1;
    });
  }

  _getOthersLabelText() {
    const a = this.getAttribute("phone-others-label");
    if (a != null && String(a).trim() !== "") return a;
    const loc = window.RoFormLocale?.phone_others;
    if (loc != null && String(loc).trim() !== "") return String(loc);
    return "Others";
  }

  _getCurrentDialCode() {
    if (!this._intlDialList) return "";
    if (this._intlSelectedIndex >= 0 && this._intlSelectedIndex < this._intlDialList.length) {
      return this._intlDialList[this._intlSelectedIndex].code;
    }
    return RoInput._digitsOnly(this._dialCustomInputEl?.value || this._intlCustomDial || "");
  }

  _displayTriggerText() {
    if (!this._intlDialList || !this._dialTriggerTextEl) return;
    if (this._intlSelectedIndex >= 0) {
      const item = this._intlDialList[this._intlSelectedIndex];
      this._dialTriggerTextEl.textContent = item.label || `+ ${item.code}`;
      return;
    }
    const custom = RoInput._digitsOnly(this._dialCustomInputEl?.value || this._intlCustomDial || "");
    if (custom) {
      this._dialTriggerTextEl.textContent = `+ ${custom}`;
    } else {
      this._dialTriggerTextEl.textContent = this._getOthersLabelText();
    }
  }

  _refreshDialTriggerText() {
    if (!this._intlMode) return;
    this._displayTriggerText();
  }

  _composeIntlValue() {
    if (!this._intlMode) return this._inputEl.value;
    const dial = this._getCurrentDialCode();
    const national = RoInput._digitsOnly(this._inputEl.value);
    if (!national) return "";
    if (!dial) return "";
    return `+${dial} ${national}`;
  }

  _applyIntlValueString(str) {
    if (!this._intlDialList) return;
    const trimmed = String(str || "").trim();
    if (!trimmed) {
      this._intlSelectedIndex = 0;
      this._intlCustomDial = "";
      this._inputEl.value = "";
      if (this._dialCustomInputEl) this._dialCustomInputEl.value = "";
      this._refreshDialTriggerText();
      return;
    }
    const parsed = RoInput._parseIntlString(trimmed);
    if (!parsed) {
      this._intlSelectedIndex = 0;
      this._intlCustomDial = "";
      this._inputEl.value = RoInput._digitsOnly(trimmed);
      this._refreshDialTriggerText();
      return;
    }
    const idx = this._intlDialList.findIndex(d => d.code === parsed.dial);
    if (idx >= 0) {
      this._intlSelectedIndex = idx;
      this._intlCustomDial = "";
      this._inputEl.value = parsed.national;
    } else {
      this._intlSelectedIndex = -1;
      this._intlCustomDial = parsed.dial;
      this._inputEl.value = parsed.national;
      if (this._dialCustomInputEl) this._dialCustomInputEl.value = parsed.dial;
    }
    this._refreshDialTriggerText();
  }

  _openDialPanel() {
    if (!this._intlMode || this.hasAttribute("disabled")) return;
    this._ensureDialPanel();
    this._populateDialPanel();
    this._dialOpen = true;
    this._dialPanelEl.hidden = false;
    this.setAttribute("dial-panel-open", "");
    this._positionDialPanel();
    this._dialTriggerEl?.setAttribute("aria-expanded", "true");
    document.addEventListener("pointerdown", this._onDocPointerDown, true);
    window.addEventListener("scroll", this._onWinScrollResize, true);
    window.addEventListener("resize", this._onWinScrollResize);
  }

  _closeDialPanel() {
    if (!this._dialOpen) return;
    this._dialOpen = false;
    this.removeAttribute("dial-panel-open");
    if (this._dialPanelEl) this._dialPanelEl.hidden = true;
    this._dialTriggerEl?.setAttribute("aria-expanded", "false");
    document.removeEventListener("pointerdown", this._onDocPointerDown, true);
    window.removeEventListener("scroll", this._onWinScrollResize, true);
    window.removeEventListener("resize", this._onWinScrollResize);
    let normalizedOthersEmpty = false;
    if (this._dialCustomInputEl) {
      this._intlCustomDial = RoInput._digitsOnly(this._dialCustomInputEl.value || "");
      if (this._intlSelectedIndex < 0 && !this._intlCustomDial) {
        this._intlSelectedIndex = 0;
        normalizedOthersEmpty = true;
      }
      this._dialCustomInputEl.removeEventListener("input", this._onDialCustomInput);
      this._dialCustomInputEl = null;
    }
    if (normalizedOthersEmpty) {
      this._refreshDialTriggerText();
      this._syncIntlValueAndNotify();
    }
  }

  _positionDialPanel() {
    if (!this._dialPanelEl || !this._dialTriggerEl || !this._containerEl) return;
    const cr = this._containerEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const margin = 8;
    const gap = 4;
    let left = cr.left;
    let top = cr.bottom + gap;
    if (this._dialPanelEl.dir === "rtl") left = Math.max(margin, cr.right - this._dialPanelEl.offsetWidth);
    this._dialPanelEl.style.left = `${Math.min(left, vw - margin - this._dialPanelEl.offsetWidth)}px`;
    this._dialPanelEl.style.top = `${top}px`;
    const ph = this._dialPanelEl.getBoundingClientRect().height;
    if (top + ph > window.innerHeight - margin) {
      top = Math.max(margin, cr.top - ph - gap);
      this._dialPanelEl.style.top = `${top}px`;
    }
  }

  _onWinScrollResize() {
    if (this._dialOpen) this._positionDialPanel();
  }

  _onDocPointerDown(e) {
    if (!this._dialOpen) return;
    const t = e.target;
    if (this._dialTriggerEl?.contains(t)) return;
    if (this._dialPanelEl?.contains(t)) return;
    this._closeDialPanel();
  }

  _onDialTriggerClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this._dialOpen) this._closeDialPanel();
    else this._openDialPanel();
  }

  _onDialPanelClick(e) {
    const btn = e.target.closest(".ro-input-dial-opt");
    if (!btn || !this._dialPanelEl?.contains(btn)) return;
    const ix = parseInt(btn.dataset.dialIndex, 10);
    if (Number.isNaN(ix)) return;
    this._intlSelectedIndex = ix;
    this._intlCustomDial = "";
    if (this._dialCustomInputEl) this._dialCustomInputEl.value = "";
    this._closeDialPanel();
    this._refreshDialTriggerText();
    this._syncIntlValueAndNotify();
  }

  _onDialCustomInput() {
    this._intlCustomDial = RoInput._digitsOnly(this._dialCustomInputEl?.value || "");
    /* Others 且无数字：回到第一项预设，避免触发器长期显示 “Others” */
    if (this._intlCustomDial === "") {
      this._intlSelectedIndex = 0;
    } else {
      this._intlSelectedIndex = -1;
    }
    this._refreshDialTriggerText();
    this._syncIntlValueAndNotify();
  }

  _syncIntlValueAndNotify() {
    const next = this._composeIntlValue();
    this._value = next;
    this._suspendValueAttr = true;
    this.setAttribute("value", next);
    this._suspendValueAttr = false;
    this._updateHasValueState();
    this._internals.setFormValue(next);
    const detail = {
      value: next,
      dialCode: this._getCurrentDialCode(),
      national: RoInput._digitsOnly(this._inputEl.value),
    };
    this._dispatch("input", detail);
    this._dispatch("change", detail);
    if (this.hasAttribute("validate-on-input")) this.validate();
  }

  /** 未设置或空 `size` → medium；非空则经 `_normalizeSize` 纠正非法值 */
  _syncDefaultSizeAttribute() {
    const raw = this.getAttribute("size");
    if (raw === null || String(raw).trim() === "") {
      this.setAttribute("size", "medium");
      return;
    }
    const n = RoInput._normalizeSize(raw);
    if (String(raw).trim().toLowerCase() !== n) this.setAttribute("size", n);
  }

  connectedCallback() {
    this._syncDefaultSizeAttribute();
    this._bindListeners();

    this._updateInputAttributes();
    this._updateLabel();
    this._updateSlotVisibility(this._prefixSlot);
    this._updateSlotVisibility(this._suffixSlot);
  }

  _bindListeners() {
    if (this._listenersBound) {
      this._inputEl.removeEventListener("input", this._onInput);
      this._inputEl.removeEventListener("focus", this._onFocus);
      this._inputEl.removeEventListener("blur", this._onBlur);
      this._inputEl.removeEventListener("change", this._onChange);
      this._containerEl.removeEventListener("click", this._onContainerClick);
      this._prefixSlot.removeEventListener("slotchange", this._onSlotChange);
      this._suffixSlot.removeEventListener("slotchange", this._onSlotChange);
    }

    this._inputEl.addEventListener("input", this._onInput);
    this._inputEl.addEventListener("focus", this._onFocus);
    this._inputEl.addEventListener("blur", this._onBlur);
    this._inputEl.addEventListener("change", this._onChange);
    this._containerEl.addEventListener("click", this._onContainerClick);
    this._prefixSlot.addEventListener("slotchange", this._onSlotChange);
    this._suffixSlot.addEventListener("slotchange", this._onSlotChange);
    this._listenersBound = true;
  }

  disconnectedCallback() {
    this._inputEl.removeEventListener("input", this._onInput);
    this._inputEl.removeEventListener("focus", this._onFocus);
    this._inputEl.removeEventListener("blur", this._onBlur);
    this._inputEl.removeEventListener("change", this._onChange);
    this._containerEl.removeEventListener("click", this._onContainerClick);
    this._prefixSlot.removeEventListener("slotchange", this._onSlotChange);
    this._suffixSlot.removeEventListener("slotchange", this._onSlotChange);
    this._listenersBound = false;
    this._destroyDialPanel();
    document.removeEventListener("pointerdown", this._onDocPointerDown, true);
    window.removeEventListener("scroll", this._onWinScrollResize, true);
    window.removeEventListener("resize", this._onWinScrollResize);
  }

  static get observedAttributes() {
    return [
      "type", "placeholder", "value", "disabled", "required", "float-label", "size", "name",
      "pattern", "min-length", "max-length", "min", "max",
      "validate-fn", "validate-on-input",
      "error-message", "error-message-required", "error-message-invalid",
      "dial-codes", "phone-others-label",
      "disable-adaptation",
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === "size") {
      this._syncDefaultSizeAttribute();
      return;
    }

    if (name === "dial-codes" || name === "phone-others-label") {
      const prevMode = this._intlMode;
      const saved = this._value;
      if (name === "dial-codes") {
        this._render();
        this._bindListeners();
        this._updateInputAttributes();
        this._updateLabel();
        this._updateSlotVisibility(this._prefixSlot);
        this._updateSlotVisibility(this._suffixSlot);
      } else if (this._intlMode) {
        this._refreshDialTriggerText();
        if (this._dialOpen) this._populateDialPanel();
      }
      if (prevMode !== this._intlMode || name === "dial-codes") {
        this._suspendValueAttr = true;
        this._updateValue(saved);
        this._suspendValueAttr = false;
      }
      return;
    }

    if (["type", "disabled", "required", "pattern", "min", "max", "min-length", "max-length", "name"].includes(name)) {
      this._updateInputAttributes();
      return;
    }
    if (["placeholder", "float-label"].includes(name)) {
      this._updateLabel();
      return;
    }
    if (name === "value") {
      if (this._suspendValueAttr) return;
      this._updateValue(newValue);
      return;
    }
    if (["error-message", "error-message-required", "error-message-invalid"].includes(name) && this._isError) {
      this.validate();
    }
  }

  _onInput(e) {
    if (this._intlMode) {
      const national = RoInput._digitsOnly(e.target.value);
      e.target.value = national;
      this._value = this._composeIntlValue();
    } else {
      this._value = e.target.value;
    }
    this._suspendValueAttr = true;
    this.setAttribute("value", this._value);
    this._suspendValueAttr = false;
    this._updateHasValueState();
    this._internals.setFormValue(this._value);
    this._dispatch("input", {
      value: this._value,
      ...(this._intlMode ? { dialCode: this._getCurrentDialCode(), national: RoInput._digitsOnly(this._inputEl.value) } : {}),
    });
    if (this.hasAttribute("validate-on-input")) this.validate();
  }

  _onFocus() {
    this._isFocused = true;
    this.setAttribute("focused", "");
    this._dispatch("focus", { value: this._value });
  }

  _onBlur() {
    this._isFocused = false;
    this.removeAttribute("focused");
    if (!this.hasAttribute("validate-on-input")) this.validate();
    this._dispatch("blur", { value: this._value });
  }

  _onChange() {
    this._dispatch("change", {
      value: this._value,
      ...(this._intlMode ? { dialCode: this._getCurrentDialCode(), national: RoInput._digitsOnly(this._inputEl.value) } : {}),
    });
  }

  _onContainerClick(e) {
    if (this.hasAttribute("disabled")) return;
    const t = e.target;
    if (t.closest && t.closest(".dial-trigger")) return;
    if (t.classList.contains("input-container") || t.classList.contains("input-wrapper") || t.classList.contains("label")) {
      this._inputEl.focus();
    }
  }

  _onSlotChange(e) {
    this._updateSlotVisibility(e.target);
  }

  _dispatch(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  validate() {
    if (this.hasAttribute("disabled")) return { valid: true, message: "" };

    const errorMessage =
      this._validateRequired() ??
      this._validateType() ??
      this._validatePattern() ??
      this._validateLength() ??
      this._validateRange() ??
      this._validateCustom() ??
      "";

    const valid = !errorMessage;
    this._setError(!valid, errorMessage);
    this._dispatch("validate", {
      valid,
      message: errorMessage,
      value: this._value,
      ...(this._intlMode ? { dialCode: this._getCurrentDialCode(), national: RoInput._digitsOnly(this._inputEl.value) } : {}),
    });
    if (!valid) {
      this._dispatch("error", {
        message: errorMessage,
        value: this._value,
        ...(this._intlMode ? { dialCode: this._getCurrentDialCode(), national: RoInput._digitsOnly(this._inputEl.value) } : {}),
      });
    }
    return { valid, message: errorMessage };
  }

  reset() {
    this._closeDialPanel();
    if (this._intlMode && this._intlDialList) {
      this._intlSelectedIndex = 0;
      this._intlCustomDial = "";
      this._inputEl.value = "";
      this._value = "";
      this._suspendValueAttr = true;
      this.setAttribute("value", "");
      this._suspendValueAttr = false;
      this._refreshDialTriggerText();
    } else {
      this._value = "";
      this._inputEl.value = "";
      this.setAttribute("value", "");
    }
    this._setError(false, "");
    this._updateHasValueState();
    this._internals.setFormValue("");
  }

  clearError() {
    this._setError(false, "");
  }

  focus() {
    this._inputEl.focus();
  }

  blur() {
    this._inputEl.blur();
  }

  _validateRequired() {
    if (!this.hasAttribute("required") || String(this._value || "").trim()) return null;
    return this._getErrorMessage("required");
  }

  _validateType() {
    if (!this._value) return null;
    const type = this.getAttribute("type") || "text";
    if (this._intlMode && type === "tel") {
      return this._validateIntlTelType();
    }
    if (type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this._value)) {
      return this._getErrorMessage("invalid_email");
    }
    if (type === "tel" && !/^[0-9\s\-+()]+$/.test(this._value)) {
      return this._getErrorMessage("invalid_phone");
    }
    if (type === "number" && isNaN(parseFloat(this._value))) {
      return this._getErrorMessage("invalid_number");
    }
    return null;
  }

  _validateIntlTelType() {
    const dial = this._getCurrentDialCode();
    const national = RoInput._digitsOnly(this._inputEl.value);
    if (!dial || !/^\d{1,4}$/.test(dial)) return this._getErrorMessage("invalid_phone_intl");
    if (!national || !/^\d+$/.test(national)) return this._getErrorMessage("invalid_phone_intl");
    if (!/^\+(\d{1,4}) \d+$/.test(this._value.trim())) return this._getErrorMessage("invalid_phone_intl");
    return null;
  }

  _validatePattern() {
    if (!this._value || !this.hasAttribute("pattern")) return null;
    const pattern = new RegExp(this.getAttribute("pattern"));
    return !pattern.test(this._value) ? this._getErrorMessage("pattern") : null;
  }

  _validateLength() {
    const raw = this._value;
    if (!raw) return null;
    const min = this.getAttribute("min-length");
    const max = this.getAttribute("max-length");
    const checkStr = this._intlMode ? RoInput._digitsOnly(this._inputEl.value) : raw;
    if (min && checkStr.length < parseInt(min, 10)) {
      return this._getErrorMessage("min_length", { min });
    }
    if (max && checkStr.length > parseInt(max, 10)) {
      return this._getErrorMessage("max_length", { max });
    }
    return null;
  }

  _validateRange() {
    if (!this._value || this.getAttribute("type") !== "number") return null;
    const min = this.getAttribute("min");
    const max = this.getAttribute("max");
    const num = parseFloat(this._value);
    if (min != null && num < parseFloat(min)) return this._getErrorMessage("min_value", { min });
    if (max != null && num > parseFloat(max)) return this._getErrorMessage("max_value", { max });
    return null;
  }

  _validateCustom() {
    if (!this._value || !this.hasAttribute("validate-fn")) return null;
    const fn = window[this.getAttribute("validate-fn")];
    return typeof fn === "function" ? fn(this._value) : null;
  }

  _updateSlotVisibility(slotEl) {
    const hasContent = slotEl.assignedNodes({ flatten: true }).some(n => n.nodeType === Node.ELEMENT_NODE);
    slotEl.classList.toggle("empty", !hasContent);
  }

  _updateInputAttributes() {
    this._inputEl.type = this.getAttribute("type") || "text";
    this._inputEl.disabled = this.hasAttribute("disabled");
    this._inputEl.required = this.hasAttribute("required");
    if (this._intlMode) {
      this._inputEl.removeAttribute("name");
    } else {
      this._inputEl.name = this.getAttribute("name") || "";
    }
    RoInput.INPUT_ATTRS.forEach(attr => {
      const val = this.getAttribute(attr);
      if (val != null) this._inputEl[attr] = val;
    });
    const minLen = this.getAttribute("min-length");
    const maxLen = this.getAttribute("max-length");
    if (minLen != null) this._inputEl.minLength = parseInt(minLen, 10);
    if (maxLen != null) this._inputEl.maxLength = parseInt(maxLen, 10);
    if (this._dialTriggerEl) this._dialTriggerEl.disabled = this.hasAttribute("disabled");
    this._updateAriaAttributes();
  }

  _updateLabel() {
    const text = this.getAttribute("placeholder") || "";
    if (this.hasAttribute("float-label")) {
      this._labelEl.textContent = text;
      this._inputEl.placeholder = "";
    } else {
      this._labelEl.textContent = "";
      this._inputEl.placeholder = text;
    }
  }

  _updateValue(value) {
    this._value = value ?? "";
    if (this._intlMode) {
      this._applyIntlValueString(this._value);
      this._value = this._composeIntlValue();
      this._inputEl.value = RoInput._digitsOnly(this._inputEl.value);
      this._suspendValueAttr = true;
      this.setAttribute("value", this._value);
      this._suspendValueAttr = false;
    } else {
      this._inputEl.value = this._value;
    }
    this._updateHasValueState();
    this._internals.setFormValue(this._value);
    this._refreshDialTriggerText();
  }

  _updateHasValueState() {
    this.toggleAttribute("has-value", !!this._value);
  }

  _setError(isError, message) {
    this._isError = isError;
    this._errorMessage = message;
    this.toggleAttribute("error", isError);
    this._errorEl.textContent = isError ? (this.getAttribute("error-message") || message) : "";
    this._updateAriaAttributes();
  }

  _updateAriaAttributes() {
    this._inputEl.toggleAttribute("aria-invalid", this._isError);
    this._inputEl.toggleAttribute("aria-required", this.hasAttribute("required"));
    if (this._isError && this._errorMessage) {
      const id = this._errorEl.id || `error-${Math.random().toString(36).slice(2)}`;
      this._errorEl.id = id;
      this._inputEl.setAttribute("aria-describedby", id);
    } else {
      this._inputEl.removeAttribute("aria-describedby");
    }
  }

  _applyParams(msg, params = {}) {
    if (msg == null || msg === "") return msg;
    let out = String(msg);
    if (params && Object.keys(params).length) {
      Object.entries(params).forEach(([k, v]) => {
        out = out.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g"), String(v));
      });
    }
    return out;
  }

  _getErrorMessage(key, params = {}) {
    const legacy = this.getAttribute("error-message");
    if (legacy) return this._applyParams(legacy, params);

    if (key === "required") {
      const a = this.getAttribute("error-message-required");
      if (a != null && a !== "") return this._applyParams(a, params);
    } else {
      const a = this.getAttribute("error-message-invalid");
      if (a != null && a !== "") return this._applyParams(a, params);
    }

    let msg = window.RoFormLocale?.[key];
    if (msg != null && msg !== "") return this._applyParams(msg, params);

    msg = RoInput.DEFAULT_MESSAGES[key];
    if (msg != null && msg !== "") return this._applyParams(msg, params);
    return "";
  }

  get value() {
    return this._value;
  }
  set value(v) {
    this.setAttribute("value", v);
  }
  get type() {
    return this.getAttribute("type") || "text";
  }
  set type(v) {
    this.setAttribute("type", v);
  }
  get disabled() {
    return this.hasAttribute("disabled");
  }
  set disabled(v) {
    this.toggleAttribute("disabled", !!v);
  }
  get required() {
    return this.hasAttribute("required");
  }
  set required(v) {
    this.toggleAttribute("required", !!v);
  }

  formResetCallback() {
    this.value = "";
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state, mode) {
    if (mode === "restore") this.value = state;
  }
}

if (!customElements.get("ro-input")) {
  customElements.define("ro-input", RoInput);
}
